<script setup lang="ts">
/** Section « Contraintes projet » (capture DT, section 7). */
import { ScaleIcon } from '@heroicons/vue/24/outline'
import type { AccompProjectConstraints } from '@/v2/accompagnement/api/accompagnementTypes'

const c = defineModel<AccompProjectConstraints>('constraints', { required: true })

function onCompetitorInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value
  c.value = { ...c.value, competitorCount: raw === '' ? null : Math.max(0, Number(raw)) }
}

function onBudgetKnownChange(e: Event) {
  const known = (e.target as HTMLInputElement).checked
  // Décocher « Budget connu » efface le montant.
  c.value = { ...c.value, budgetKnown: known, budgetAmount: known ? c.value.budgetAmount : null }
}

function onBudgetAmountInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value
  c.value = { ...c.value, budgetAmount: raw === '' ? null : Math.max(0, Number(raw)) }
}
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="accomp-constraints-title">
    <div class="card-body gap-4 p-4 md:p-6">
      <h2 id="accomp-constraints-title" class="card-title flex items-center gap-2 text-lg font-semibold">
        <ScaleIcon class="h-5 w-5 text-primary" aria-hidden="true" />
        Contraintes projet
      </h2>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="cstr-desired" class="mb-1 block text-sm font-medium text-base-content">Date souhaitée</label>
          <input id="cstr-desired" v-model="c.desiredDate" type="date" class="input input-bordered w-full" />
        </div>
        <div>
          <label for="cstr-mandatory" class="mb-1 block text-sm font-medium text-base-content">Date impérative</label>
          <input id="cstr-mandatory" v-model="c.mandatoryDate" type="date" class="input input-bordered w-full" />
        </div>

        <div>
          <label for="cstr-decision" class="mb-1 block text-sm font-medium text-base-content">Décisionnaire</label>
          <input
            id="cstr-decision"
            v-model="c.decisionMaker"
            type="text"
            class="input input-bordered w-full"
            placeholder="Nom / fonction"
          />
        </div>
        <div>
          <label for="cstr-competitors" class="mb-1 block text-sm font-medium text-base-content">
            Nombre de concurrents
          </label>
          <input
            id="cstr-competitors"
            type="number"
            min="0"
            class="input input-bordered w-full"
            :value="c.competitorCount ?? ''"
            @input="onCompetitorInput"
          />
        </div>

        <label class="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            class="toggle toggle-primary"
            :checked="c.budgetKnown"
            @change="onBudgetKnownChange"
          />
          <span class="text-base">Budget connu</span>
        </label>
        <label class="flex cursor-pointer items-center gap-2">
          <input v-model="c.tender" type="checkbox" class="toggle toggle-primary" />
          <span class="text-base">Appel d'offres</span>
        </label>

        <!-- Montant révélé si le budget est connu -->
        <div v-if="c.budgetKnown" class="md:col-span-2 sm:max-w-xs">
          <label for="cstr-budget-amount" class="mb-1 block text-sm font-medium text-base-content">
            Budget estimé (€)
          </label>
          <input
            id="cstr-budget-amount"
            type="number"
            min="0"
            step="100"
            class="input input-bordered w-full"
            placeholder="Ex. 5000"
            :value="c.budgetAmount ?? ''"
            @input="onBudgetAmountInput"
          />
        </div>
      </div>
    </div>
  </section>
</template>
